import { useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import {
  Undo as UndoIcon,
  Redo as RedoIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';

interface BpmnEditorToolbarProps {
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitViewport: () => void;
  onExportSvg: () => Promise<string | null>;
  onNewDiagram: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isDirty: boolean;
  isSaving: boolean;
  processDefinitionKey?: string;
}

export function BpmnEditorToolbar({
  onSave,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onFitViewport,
  onExportSvg,
  onNewDiagram,
  canUndo,
  canRedo,
  isDirty,
  isSaving,
  processDefinitionKey,
}: BpmnEditorToolbarProps) {
  const handleExportSvg = useCallback(async () => {
    const svg = await onExportSvg();
    if (!svg) return;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${processDefinitionKey || 'diagram'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [onExportSvg, processDefinitionKey]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Tooltip title="Neues Diagramm">
        <IconButton size="small" onClick={onNewDiagram}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Tooltip title="Rueckgaengig">
        <span>
          <IconButton size="small" onClick={onUndo} disabled={!canUndo}>
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Wiederholen">
        <span>
          <IconButton size="small" onClick={onRedo} disabled={!canRedo}>
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Tooltip title="Vergroessern">
        <IconButton size="small" onClick={onZoomIn}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Verkleinern">
        <IconButton size="small" onClick={onZoomOut}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Einpassen">
        <IconButton size="small" onClick={onFitViewport}>
          <FitScreenIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Tooltip title="Als SVG exportieren">
        <IconButton size="small" onClick={handleExportSvg}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ flexGrow: 1 }} />

      {isDirty && (
        <Chip
          label="Nicht gespeichert"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ mr: 1 }}
        />
      )}

      <Button
        variant="contained"
        size="small"
        startIcon={<SaveIcon />}
        onClick={onSave}
        disabled={!isDirty || isSaving}
      >
        {isSaving ? 'Speichern...' : 'Speichern'}
      </Button>
    </Box>
  );
}
